'use client'

import { cn } from '@utils'
import { useEffect, useRef, useState } from 'react'
import { CollapseContext, useCollapseContext } from './context'

export function Collapse({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLUListElement>) {
  return (
    <ul className={cn('border-t border-secondary-light', className)} {...props}>
      {children}
    </ul>
  )
}

export function CollapseItem({
  title,
  icon,
  itemKey,
  children,
}: {
  title: string
  itemKey: React.Key
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>()
  const { currentKey, changeKey } = useCollapseContext()
  const isActive = currentKey === itemKey

  useEffect(() => {
    if (contentRef.current) {
      function handleResize() {
        setHeight(contentRef.current?.offsetHeight || 0)
      }
      // 初始化时设置高度
      handleResize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [currentKey])

  function handleClick() {
    if (isActive) {
      // 如果当前索引已选中，则折叠
      changeKey(null)
    } else {
      // 否则展开当前CollapseItem
      changeKey(itemKey)
    }
  }
  return (
    <li
      className={cn('border-b border-secondary-light', { 'pb-11.5': isActive })}
    >
      <div
        className={cn(
          'flex items-center justify-between h-20 cursor-pointer group/collapseItem hover:h-22 transition-[height]',
          { 'h-22': isActive }
        )}
        onClick={handleClick}
      >
        <span
          className={cn(
            'text-xl lg:text-2xl group-hover/collapseItem:text-primary line-clamp-1',
            {
              'text-primary': isActive,
            }
          )}
        >
          {title}
        </span>
        {
          icon && (
            <span className='shrink-0'>{icon}</span>
          )
        }
      </div>
      <div
        className={cn(
          'transition-[height] origin-top duration-350 overflow-hidden'
        )}
        style={{ height: isActive ? `${height}px` : '0px' }}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </li>
  )
}

export function CollapseContextProvider({
  defaultKey = null,
  children,
}: {
  defaultKey?: React.Key | null
  children: React.ReactNode
}) {
  const [currentKey, changeKey] = useState<React.Key | null>(defaultKey)

  return (
    <CollapseContext.Provider value={{ currentKey, changeKey }}>
      {children}
    </CollapseContext.Provider>
  )
}
